"use client";

import { useRef, useEffect, useState } from "react";
import { FormRenderer } from "../form-renderer";
import { getEmbedSearchString } from "../../utils/url-params";
import {
  useResizeObserver,
  sendFormSubmitSuccess,
  sendFormError,
  useIsEmbedded,
} from "../../utils/embedMessaging";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Globally override non-error toast types to only allow error toasts in embed mode
if (typeof window !== "undefined") {
  try {
    (toast as any).success = () => "";
    (toast as any).info = () => "";
    (toast as any).warning = () => "";
  } catch (e) {
    console.warn("Could not patch toast library:", e);
  }
}

export interface EmbedFormRendererProps {
  formId: string | number;
  apiBaseUrl: string; // API base URL for fetching form data
  formAnswers?: Record<string, any>; // Optional pre-loaded form answers (FormRenderer will auto-fetch if empty)
  initialValues?: Record<string, any>; // Optional initial values from props
  redirectUrl?: string; // Redirect URL from query params
  webhookUrl?: string; // Webhook URL to trigger on form completion
  width?: string; // Optional width override
  height?: string; // Optional height override
  onError?: (error: string) => void;
}

/**
 * EmbedFormRenderer - Standalone form renderer optimized for iframe embedding
 *
 * Features:
 * - Minimal UI chrome (no headers, borders)
 * - Auto-resize via ResizeObserver and PostMessage
 * - PostMessage communication with parent window
 * - URL query parameter parsing for redirect URL
 * - Handles form completion with optional parent redirect
 * - FormRenderer auto-fetches saved answers from backend if not provided
 */
export function EmbedFormRenderer({
  formId,
  apiBaseUrl,
  formAnswers,
  initialValues: propInitialValues,
  redirectUrl: propRedirectUrl,
  webhookUrl: propWebhookUrl,
  width,
  height,
  onError,
}: EmbedFormRendererProps) {
  console.log('🎯 EmbedFormRenderer rendered with props:', {
    formId,
    formIdType: typeof formId,
    apiBaseUrl,
    redirectUrl: propRedirectUrl,
    webhookUrl: propWebhookUrl,
    hasFormAnswers: !!formAnswers,
    hasInitialValues: !!propInitialValues
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const [currentHeight, setCurrentHeight] = useState<number>(600);
  const isEmbedded = useIsEmbedded();

  // Parse URL query params for redirect URL and webhook URL
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(propRedirectUrl);
  const [webhookUrl, setWebhookUrl] = useState<string | undefined>(propWebhookUrl);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const searchString = getEmbedSearchString();
      const params = new URLSearchParams(searchString);
      
      if (!redirectUrl) {
        const urlRedirect = params.get("redirect") || params.get("redirectUrl");
        if (urlRedirect) {
          setRedirectUrl(urlRedirect);
        }
      }
      
      if (!webhookUrl) {
        const urlWebhook = params.get("webhook") || params.get("webhookUrl");
        if (urlWebhook) {
          setWebhookUrl(urlWebhook);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Set up auto-resize observer
  useResizeObserver(
    containerRef,
    (height) => {
      setCurrentHeight(height);
    },
    formId,
    300 // 300ms debounce
  );

  // Handle form completion
  const handleComplete = async (data: any) => {
    console.log("Form completed:", data);

    // Trigger external webhook if provided
    if (webhookUrl) {
      try {
        console.log("🚀 Triggering external webhook...", webhookUrl);
        
        // Format answers to match the metadata structure used in page submissions
        const formattedAnswers: Record<string, any> = {};
        const currentTimestamp = new Date().toISOString();
        
        Object.entries(data.answers || {}).forEach(([fieldId, value]) => {
          // Identify if the field is a hidden field (which is typically populated via URL params)
          const field = data.formVersion?.form_structure?.fields?.find((f: any) => f.id === fieldId);
          const answerType = field?.type === 'hidden' ? 'url-param' : 'user-entered';

          formattedAnswers[fieldId] = {
            value: value,
            type: answerType,
            userId: "unknown", // You can pass actual userId if available in context
            timestamp: currentTimestamp
          };
        });

        const response = await fetch(webhookUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            form_id: formId,
            session_id: data.sessionId || data.response?.session_id || null,
            answers: formattedAnswers,
            timestamp: currentTimestamp
          })
        });

        if (!response.ok) {
          let errorMessage = `Submission failed with status ${response.status}`;
          try {
            const errorData = await response.json();
            if (errorData?.message) {
              errorMessage = errorData.message;
            } else if (errorData?.error) {
              errorMessage = errorData.error;
            }
          } catch {
            // Fall back to text if not JSON
            const errorText = await response.text().catch(() => "");
            if (errorText) errorMessage = errorText;
          }
          throw new Error(errorMessage);
        }

        console.log("✅ Webhook triggered successfully");
      } catch (error) {
        console.error("❌ Failed to trigger webhook:", error);
        throw error; // Rethrow to block submission in FormRenderer!
      }
    }

    // Send success message to parent
    if (isEmbedded) {
      sendFormSubmitSuccess(formId, redirectUrl);
    }

    // If not embedded or no redirect URL, just show success in iframe
    if (!redirectUrl) {
      console.log("Form completed successfully");
    }
  };

  // Handle form errors
  const handleError = (error: string) => {
    console.error("Form error:", error);

    if (isEmbedded) {
      sendFormError(formId, error);
    }

    if (onError) {
      onError(error);
    }
  };

  // Handle height changes from FormRenderer
  const handleHeightChange = (height: number) => {
    setCurrentHeight(height);
  };

  console.log('📦 Rendering FormRenderer with formId:', formId);

  return (
    <div
      ref={containerRef}
      className="w-full min-h-screen"
      style={{
        width: width || "100%",
        minHeight: height || "600px",
        margin: 0,
        padding: 0,
      }}
    >
      <FormRenderer
        formId={formId}
        formAnswers={formAnswers}
        initialValues={propInitialValues}
        autoPopulateFromUrl={true}
        embeddedMode={true}
        embedRedirectUrl={redirectUrl}
        onComplete={handleComplete}
        onError={handleError}
        onHeightChange={handleHeightChange}
        className="w-full"
        askBeforeContinuingSession={{
          enabled: true,
          title: "Welcome Back!",
          message: "You have an unfinished form. Would you like to continue where you left off?",
          continueButtonText: "Yes, Continue",
          startNewButtonText: "Start Over",
        }}
      />
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </div>
  );
}

export default EmbedFormRenderer;
